import React, { FC } from 'react';
import { useParams, Navigate, Link, useLocation } from 'react-router-dom';
import { PageHero, CtaBanner } from '../components/SharedComponents';
import EnquiryForm from '../components/EnquiryForm';
import { IMG, WHATSAPP_LINK } from '../data/constants';
import {
  BARCODE_LABELS_SUBCATS, TTR_RIBBONS_SUBCATS, PRINTERS_SUBCATS,
  SCANNERS_SUBCATS, JEWELLERY_SUBCATS, TAFFETA_SUBCATS, RETAIL_POS_SUBCATS,
} from '../data/products';
import { SubCategory } from '../types';
import {
  CheckCircle, Phone, MessageCircle, ArrowLeft, Tag, Layers, ChevronRight
} from 'lucide-react';

const PARENT_MAP: Record<string, { title: string; path: string; subcats: SubCategory[]; ctaImage: string }> = {
  'barcode-labels':   { title: 'Barcode Labels',           path: '/barcode-labels',   subcats: BARCODE_LABELS_SUBCATS, ctaImage: IMG.ctaLabels   },
  'ttr-ribbons':      { title: 'Thermal Transfer Ribbons', path: '/ttr-ribbons',       subcats: TTR_RIBBONS_SUBCATS,    ctaImage: IMG.ctaRibbons  },
  'printers':         { title: 'Barcode Printers',         path: '/printers',          subcats: PRINTERS_SUBCATS,       ctaImage: IMG.ctaPrinter  },
  'scanners':         { title: 'Barcode Scanners',         path: '/scanners',          subcats: SCANNERS_SUBCATS,       ctaImage: IMG.ctaScanner  },
  'jewellery-labels': { title: 'Jewellery Labels',         path: '/jewellery-labels',  subcats: JEWELLERY_SUBCATS,      ctaImage: IMG.ctaJewellery},
  'taffeta-labels':   { title: 'Taffeta Labels',           path: '/taffeta-labels',    subcats: TAFFETA_SUBCATS,        ctaImage: IMG.ctaTaffeta  },
  'retail-pos':       { title: 'Retail POS / Billing',     path: '/retail-pos',        subcats: RETAIL_POS_SUBCATS,     ctaImage: IMG.ctaPOS      },
};

const SubcategoryDetailPage: FC = () => {
  const { subcatSlug } = useParams<{ subcatSlug: string }>();
  const { pathname } = useLocation();

  // Extract parentId from URL: /barcode-labels/thermal-direct-labels → barcode-labels
  const parentId = pathname.split('/').filter(Boolean)[0] || '';
  const parent = PARENT_MAP[parentId];
  if (!parent) return <Navigate to="/products" replace />;

  const subcat = parent.subcats.find(s => s.slug === subcatSlug);
  if (!subcat) return <Navigate to={parent.path} replace />;

  const otherSubcats = parent.subcats.filter(s => s.slug !== subcatSlug).slice(0, 4);

  return (
    <>
      <PageHero
        breadcrumbs={[
          { label: 'Home', link: '/' },
          { label: 'Products', link: '/products' },
          { label: parent.title, link: parent.path },
          { label: subcat.name },
        ]}
        title={subcat.name}
        subtitle={subcat.description}
      />

      <section style={{ background: 'var(--light)' }}>
        <div className="container">
          <div className="prod-cat-layout">
            {/* LEFT — Main Content */}
            <div className="prod-cat-main">
              <img
                className="prod-hero-img"
                src={subcat.image}
                alt={subcat.name}
                onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }}
              />

              <span className="section-tag">Product Details</span>
              <h2 style={{ fontFamily: 'Montserrat', fontWeight: 900, fontSize: 28, color: 'var(--primary)', marginBottom: 14 }}>
                {subcat.name}
              </h2>

              {subcat.fullDescription && (
                <p className="prod-cat-desc">{subcat.fullDescription}</p>
              )}

              {/* Key Features */}
              {subcat.features && subcat.features.length > 0 && (
                <>
                  <h3 style={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: 20, color: 'var(--primary)', marginBottom: 16, marginTop: 28 }}>
                    Key Features
                  </h3>
                  <ul className="check-list">
                    {subcat.features.map((f, i) => <li key={i}><CheckCircle size={16} />{f}</li>)}
                  </ul>
                </>
              )}

              {/* Specs */}
              {subcat.specs && subcat.specs.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--border)', padding: 24, marginTop: 32 }}>
                  <h3 style={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: 18, color: 'var(--primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Layers size={18} color="var(--accent)" /> Technical Specifications
                  </h3>
                  <div className="specs-grid">
                    {subcat.specs.map((s, i) => {
                      const [label, val] = s.split(':');
                      return val ? (
                        <div key={i} className="spec-row">
                          <span className="spec-label">{label.trim()}</span>
                          <span className="spec-val">{val.trim()}</span>
                        </div>
                      ) : (
                        <div key={i} className="spec-row spec-row-full">
                          <span className="spec-val">{s}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Other Variants */}
              <div style={{ marginTop: 40, padding: 24, background: '#fff', borderRadius: 12, border: '1px solid var(--border)' }}>
                <h3 style={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: 18, color: 'var(--primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag size={18} color="var(--accent)" /> Other {parent.title} Variants
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {otherSubcats.map(sc => (
                    <Link
                      key={sc.slug}
                      to={`/${parentId}/${sc.slug}`}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', borderRadius: 8, border: '1px solid var(--border)',
                        textDecoration: 'none', color: 'var(--primary)',
                        fontFamily: 'Montserrat', fontWeight: 600, fontSize: 13,
                        transition: 'all 0.2s', background: 'var(--light)',
                      }}
                    >
                      <span>{sc.name}</span>
                      <ChevronRight size={16} color="var(--accent)" />
                    </Link>
                  ))}
                  <Link
                    to={parent.path}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '12px 16px', borderRadius: 8,
                      background: 'rgba(230,48,39,0.07)', border: '1px solid rgba(230,48,39,0.2)',
                      textDecoration: 'none', color: 'var(--accent)',
                      fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13, marginTop: 4,
                    }}
                  >
                    <ArrowLeft size={14} /> View All {parent.title}
                  </Link>
                </div>
              </div>
            </div>

            {/* RIGHT — Sticky Enquiry Form */}
            <div className="side-enquiry">
              <EnquiryForm productName={subcat.name} />
              <div style={{ background: 'var(--primary)', borderRadius: 12, padding: 22, marginTop: 16, color: '#fff' }}>
                <p style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Phone size={14} /> Call Us Directly
                </p>
                <a href="tel:+919319299973" style={{ color: 'var(--accent2)', fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 6, textDecoration: 'none' }}>+91 9319299973</a>
                <a href="tel:+919319299974" style={{ color: 'var(--accent2)', fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 14, textDecoration: 'none' }}>+91 9319299974</a>
                <p style={{ fontSize: 12, color: '#64748b' }}>Mon–Sat: 9:30 AM – 6:30 PM</p>
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Dehradun, Uttarakhand</p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#25d366', color: '#fff', fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13, padding: '10px 16px', borderRadius: 8, textDecoration: 'none', marginTop: 14, justifyContent: 'center' }}>
                  <MessageCircle size={16} /> WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner image={parent.ctaImage}
        title={`Need ${subcat.name}?`}
        subtitle="Custom sizes, materials and quantities available. Bulk pricing on request."
        primaryBtnText="Get Quote →" primaryBtnLink="/contact"
        secondaryBtnText="WhatsApp Us" secondaryBtnLink={WHATSAPP_LINK} secondaryIsExternal />
    </>
  );
};

export default SubcategoryDetailPage;
